import { BillingInterval, Invoice, InvoiceStatus, OrganizationStatus, OrganizationTierCategory, OrgIdsByFeatureFlagConfig, SubscriptionInterval, Tier, TierCodes } from '@graphql';
import dayjs from 'dayjs';

export enum PlatformFeatures {
  FINANCIAL_WELLNESS = 'financialWellness',
  HRIS = 'hris',
  EQUITY = 'equity',
  TCR = 'tcr',
}

export const platformFeaturesLabels = {
  [PlatformFeatures.FINANCIAL_WELLNESS]: 'Financial Wellness',
  [PlatformFeatures.TCR]: 'Total Compensation & Rewards',
  [PlatformFeatures.HRIS]: 'HRIS Platform Integration',
  [PlatformFeatures.EQUITY]: 'Equity',
};

export const platformFeatureOptions = [
  {
    label: 'Total Compensation & Rewards',
    value: PlatformFeatures.TCR,
  },
  {
    label: 'Financial Wellness',
    value: PlatformFeatures.FINANCIAL_WELLNESS,
  },
];


export const dashedBorder = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='16' ry='16' stroke='%23445488' stroke-width='2' stroke-dasharray='20%2c 16%2c 16' stroke-dashoffset='21' stroke-linecap='square'/%3e%3c/svg%3e")`;

export enum CreateOrganizationSteps {
  ORGANIZATION_DETAILS = 'organizationDetails',
  ADMIN_DETAILS = 'adminDetails',
  BILLING_DETAILS = 'billingDetails',
}

export const CreateOrganizationStepConfig = {
  [CreateOrganizationSteps.ORGANIZATION_DETAILS]: {
    back: null,
    next: CreateOrganizationSteps.BILLING_DETAILS,
  },
  [CreateOrganizationSteps.BILLING_DETAILS]: {
    back: CreateOrganizationSteps.ORGANIZATION_DETAILS,
    next: CreateOrganizationSteps.ADMIN_DETAILS,
  },
  [CreateOrganizationSteps.ADMIN_DETAILS]: {
    back: CreateOrganizationSteps.BILLING_DETAILS,
    next: null,
  },
}

export enum OrganizationUpdateEnum {
  UPDATE = 'update',
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
}

export const isOrgTCRFeatureFlagActive = (
  orgId: string,
  featureFlagConfig: OrgIdsByFeatureFlagConfig[],
) => {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  return featureFlagConfig.reduce((acc, curr) => acc || curr?.orgIds?.includes(orgId), false);
};

/**
 * The options for the organization status
 */
export const organizationStatusOptions = Object.values(OrganizationStatus).map((status) => ({
  label: status,
  value: status as OrganizationStatus,
}));

/**
 * Format the date string to the format of month day, year
 * @param dateString - The date string to format
 * @returns The formatted date string
 * @example
 * formatDate('2025-01-01') // 'Jan 01, 2025'
 */
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Calculate the next renewal date based on the interval and started at
 * @param interval - The interval of the billing
 * @param startedAt - The started at date
 * @returns The next renewal date
 * @example
 * calculateNextRenewalDate(BillingInterval.monthly, '2025-01-01') // '2025-02-01'
 * calculateNextRenewalDate(BillingInterval.yearly, '2025-01-01') // '2026-01-01'
 */
export const calculateNextRenewalDate = (interval: BillingInterval | SubscriptionInterval, startedAt: string) => {
  const now = dayjs();

  if (!startedAt) {
    return now.add(1, 'month').toISOString();
  }

  const startDate = dayjs(startedAt || '');

  if ([SubscriptionInterval.monthly, BillingInterval.monthly].includes(interval)) {
    // Set the year to the current year
    const nextRenewalDate = startDate.add(1, 'month').set('year', now.year());
    return nextRenewalDate.toISOString();
  }

  // Set the year to the next year
  const nextRenewalDate = startDate.set('year', now.year() + 1);
  return nextRenewalDate.toISOString();
}

export enum PaymentStatusEnum {
  Failed = 'Failed',
  Paid = 'Paid',
  Pending = 'Pending',
  Draft = 'Draft',
  Closed = 'Closed',
}

/**
 * Calculate the payment status for an invoice
 * @param invoice - The invoice to calculate the payment status for
 * @returns The payment status
 * @example
 * calculatePaymentStatus({ status: InvoiceStatus.open, attemptCount: 1 }) // PaymentStatus.Failed
 * calculatePaymentStatus({ status: InvoiceStatus.paid }) // PaymentStatus.Paid
 * calculatePaymentStatus({ status: InvoiceStatus.draft }) // PaymentStatus.Draft
 * calculatePaymentStatus({ status: InvoiceStatus.uncollectible }) // PaymentStatus.Failed
 * calculatePaymentStatus({ status: InvoiceStatus.void }) // PaymentStatus.Failed
 */
export const calculatePaymentStatus = (invoice: Invoice) => {
  if (invoice.status === InvoiceStatus.open && invoice.attemptCount > 0) {
    return PaymentStatusEnum.Failed;
  }

  if (invoice.status === InvoiceStatus.paid) {
    return PaymentStatusEnum.Paid;
  }

  if (invoice.status === InvoiceStatus.open) {
    return PaymentStatusEnum.Pending;
  }

  if (invoice.status === InvoiceStatus.draft) {
    return PaymentStatusEnum.Draft;
  }

  if ([InvoiceStatus.void, InvoiceStatus.uncollectible].includes(invoice.status)) {
    return PaymentStatusEnum.Closed;
  }

  return PaymentStatusEnum.Failed;
}

/**
 * Get the plan for an invoice
 * @param invoice - The invoice to get the plan for
 * @param tiers - The tiers to get the plan from
 * @returns The plan for the invoice
 * @example
 * getInvoicePlan({ lines: [{ product: 'product_1' }] }, [{ stripeProductId: 'product_1', name: 'Plan 1' }]) // 'Plan 1'
 * getInvoicePlan({ lines: [{ product: 'product_2' }] }, [{ stripeProductId: 'product_1', name: 'Plan 1' }]) // '-'
 */
export const getInvoicePlan = (invoice: Invoice, tiers: Tier[]) => {
  const invoiceLineProductIds = invoice.lines.map((line) => line.product);

  const invoiceTier = tiers.find((tier) => invoiceLineProductIds.includes(tier.stripeProductId));
  return invoiceTier?.name || '-';
}

/**
 * 
 * @param invoice - The invoice to get the billing date for
 * @returns The billing date for the invoice
 */
export const getInvoiceBillingDate = (invoice: Invoice) => {
  if (invoice.status === InvoiceStatus.paid) {
    return invoice.paidAt;
  }

  return invoice.dueDate || invoice.periodEnd;
}

export const coreTierCodes = [TierCodes.CORE_V1];
export const proTierCodes = [TierCodes.PRO_V1];
export const enterpriseTierCodes = [TierCodes.ENTERPRISE_V1];

/**
 * Get the billing category for a tier code
 * @param tierCode - The tier code to get the billing category for
 * @returns The billing category for the tier code
 * @example
 * getBillingCategory(TierCodes.CORE_V1) // OrganizationTierCategory.Core
 * getBillingCategory(TierCodes.PRO_V1) // OrganizationTierCategory.Pro
 * getBillingCategory(TierCodes.ENTERPRISE_V1) // OrganizationTierCategory.Enterprise
 */
export const getBillingCategory = (tierCode: TierCodes) => {
  if (coreTierCodes.includes(tierCode)) {
    return OrganizationTierCategory.Core;
  }

  if (proTierCodes.includes(tierCode)) {
    return OrganizationTierCategory.Pro;
  }

  return OrganizationTierCategory.Enterprise;
}

export const TierHierarchy = [coreTierCodes, proTierCodes, enterpriseTierCodes];

/**
 * 
 * @param tierCode - The tier code to get the hierarchy for
 * @returns The hierarchy for the tier code
 * @example
 * getTierHierarchy(TierCodes.CORE_V1) // 0
 * getTierHierarchy(TierCodes.PRO_V1) // 1
 * getTierHierarchy(TierCodes.ENTERPRISE_V1) // 2
 */
export const getTierHierarchy = (tierCode: TierCodes) => TierHierarchy.findIndex((tier) => tier.includes(tierCode));

/**
 * Compare two tiers
 * @param tierCodeA - The first tier code to compare
 * @param tierCodeB - The second tier code to compare
 * @returns The comparison result
 * @example
 * compareTiers(TierCodes.CORE_V1, TierCodes.PRO_V1) // -1
 * compareTiers(TierCodes.PRO_V1, TierCodes.ENTERPRISE_V1) // -1
 * compareTiers(TierCodes.ENTERPRISE_V1, TierCodes.CORE_V1) // 1
 */
export const compareTiers = (tierCodeA: TierCodes, tierCodeB: TierCodes) => {
  const hierarchyA = getTierHierarchy(tierCodeA);
  const hierarchyB = getTierHierarchy(tierCodeB);

  return hierarchyB - hierarchyA;
}